import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  User,
  Settings as SettingsIcon,
  Bell,
  Shield,
  CreditCard,
  Building,
  Save,
  Database
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile, getErrorMessage } from "../services/api";
import { toast } from 'react-hot-toast';
import RoleBasedWrapper from '../components/ui/role-based-wrapper';
import DataIntegrityWidget from '../components/data/DataIntegrityWidget';


const Settings = () => {
  const { user, isOwner } = useAuth();
  const [loading, setLoading] = useState(false);

  // User Profile State - matches database schema
  const [userProfile, setUserProfile] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    business_name: user?.business_name || ''
  });

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    low_stock_alerts: true,
    payment_reminders: true,
    sales_reports: true,
    new_customer_alerts: false
  });



  const handleUserProfileUpdate = async () => {
    try {
      setLoading(true);
      console.log('[SETTINGS] Updating user profile:', userProfile);

      // Only send fields that exist in the database
      const profileData = {
        full_name: userProfile.full_name,
        business_name: userProfile.business_name,
        // Note: email and phone typically require special verification processes
        // so we might not want to allow changing them here
      };

      const response = await updateProfile(profileData);
      console.log('[SETTINGS] Profile update response:', response);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      const errorMessage = getErrorMessage(error, 'Failed to update profile');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInlineUpgrade = async (planId) => {
    if (!user?.email) {
      toast.error('User email not found. Please refresh and try again.');
      return;
    }

    try {
      setUpgradeLoading(true);
      console.log('[SETTINGS] Starting inline upgrade to plan:', planId);
      
      // Define plan data based on planId
      const planData = {
        weekly: {
          id: 'weekly',
          name: 'Silver Weekly',
          price: 140000, // ₦1,400 in kobo
          displayPrice: '₦1,400',
          period: '/week'
        },
        monthly: {
          id: 'monthly', 
          name: 'Silver Monthly',
          price: 450000, // ₦4,500 in kobo
          displayPrice: '₦4,500',
          period: '/month'
        },
        yearly: {
          id: 'yearly',
          name: 'Silver Yearly', 
          price: 5000000, // ₦50,000 in kobo
          displayPrice: '₦50,000',
          period: '/year'
        }
      };
      
      const plan = planData[planId];
      if (!plan) {
        toast.error('Invalid plan selected.');
        return;
      }
      
      // Use PaystackService to process the payment
      await PaystackService.processPayment(
        plan,
        user,
        // Success callback
        (result) => {
          console.log('[SETTINGS] Payment successful:', result);
          toast.success(`Successfully upgraded to ${plan.name}!`);
          // Refresh the page to update subscription status
          window.location.reload();
        },
        // Cancel callback
        () => {
          console.log('[SETTINGS] Payment cancelled by user');
          toast.info('Payment cancelled.');
          setUpgradeLoading(false);
        },
        // Error callback
        (error) => {
          console.error('[SETTINGS] Payment failed:', error);
          toast.error(error.message || 'Payment failed. Please try again.');
          setUpgradeLoading(false);
        }
      );
      
    } catch (error) {
      console.error('[SETTINGS] Error during inline upgrade:', error);
      toast.error('Upgrade failed. Please try again later.');
      setUpgradeLoading(false);
    }
  };

  const handleNotificationSettingsUpdate = async () => {
    try {
      setLoading(true);
      console.log('[SETTINGS] Updating notification settings:', notificationSettings);
      toast.success('Notification settings updated successfully!');
    } catch (error) {
      console.error('[SETTINGS] Error updating notification settings:', error);
      toast.error(getErrorMessage(error) || 'Failed to update notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan) => {
    if (!user?.email) {
      toast.error('User email not found. Please refresh and try again.');
      return;
    }

    setLoading(true);
    
    try {
      await PaystackService.processPayment(
        plan,
        user,
        // Success callback
        (result) => {
          console.log('Payment and subscription update successful:', result);
          toast.success(`Successfully upgraded to ${plan.name}!`);
          
          // Refresh the page to show updated subscription
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        },
        // Error callback
        (error) => {
          console.error('Payment processing error:', error);
          toast.error(error.message || 'Payment processing failed. Please try again.');
          setLoading(false);
        },
        // Cancel callback
        () => {
          console.log('Payment cancelled by user');
          toast.error('Payment was cancelled');
          setLoading(false);
        }
      );
      
    } catch (error) {
      console.error('Payment initialization error:', error);
      toast.error(error.message || 'Failed to initialize payment');
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-4 space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600 text-sm sm:text-base">Manage your account and preferences</p>
          </div>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="subscription" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Subscription
            </TabsTrigger>
            <TabsTrigger value="system" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              System
            </TabsTrigger>
          </TabsList>

          {/* User Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  User Profile
                </CardTitle>
                <CardDescription>
                  Update your personal and business information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      value={userProfile.full_name}
                      onChange={(e) => setUserProfile(prev => ({
                        ...prev,
                        full_name: e.target.value
                      }))}
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="business_name">Business Name</Label>
                    <Input
                      id="business_name"
                      value={userProfile.business_name}
                      onChange={(e) => setUserProfile(prev => ({
                        ...prev,
                        business_name: e.target.value
                      }))}
                      placeholder="Enter business name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={userProfile.email}
                      disabled
                      className="bg-gray-50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Contact support to change your email address
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      value={userProfile.phone}
                      disabled
                      className="bg-gray-50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Contact support to change your phone number
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Account Information</Label>
                  <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                    <p className="text-sm"><span className="font-medium">Role:</span> {user?.role || 'User'}</p>
                    <p className="text-sm"><span className="font-medium">Subscription:</span> {user?.subscription_plan || 'Free'}</p>
                    <p className="text-sm"><span className="font-medium">Status:</span> {user?.subscription_status || 'Trial'}</p>
                    {user?.trial_days_left !== undefined && (
                      <p className="text-sm"><span className="font-medium">Trial Days Left:</span> {user.trial_days_left}</p>
                    )}
                  </div>
                </div>

                <Button
                  onClick={handleUserProfileUpdate}
                  disabled={loading}
                  className="w-full md:w-auto"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Profile'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Notification Settings
                </CardTitle>
                <CardDescription>
                  Configure how you want to receive notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  {Object.entries(notificationSettings).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <Label htmlFor={key} className="text-sm font-medium">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Label>
                        <p className="text-sm text-gray-600">
                          {key === 'email_notifications' && 'Receive notifications via email'}
                          {key === 'low_stock_alerts' && 'Get notified when products are running low'}
                          {key === 'payment_reminders' && 'Reminders for overdue payments'}
                          {key === 'sales_reports' && 'Daily and weekly sales reports'}
                          {key === 'new_customer_alerts' && 'Notifications for new customer registrations'}
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        id={key}
                        checked={value}
                        onChange={(e) => setNotificationSettings(prev => ({
                          ...prev,
                          [key]: e.target.checked
                        }))}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                    </div>
                  ))}
                </div>
                <Button
                  onClick={handleNotificationSettingsUpdate}
                  disabled={loading}
                  className="w-full md:w-auto"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Notification Settings'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab - Only for Owners */}
          <TabsContent value="subscription">
            <RoleBasedWrapper allowedRoles={['owner']} fallback={
              <Card>
                <CardContent className="p-6">
                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertDescription>
                      Only business owners can manage subscription settings. Contact your business owner for subscription changes.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            }>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Subscription & Billing
                  </CardTitle>
                  <CardDescription>
                    Manage your subscription plan and billing information
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertDescription>
                      You are currently on the <strong>{user?.subscription_plan || 'Free'}</strong> plan.
                      {user?.subscription_status === 'trial' && user?.trial_days_left !== undefined && (
                        <span> You have {user.trial_days_left} days left in your trial.</span>
                      )}
                    </AlertDescription>
                  </Alert>

                  <div className="mt-6">
                    {/* Plans Grid - Exact copy from SubscriptionUpgrade page */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {[
                        {
                          id: 'weekly',
                          name: 'Silver Weekly',
                          price: 140000,
                          displayPrice: '₦1,400',
                          period: '/week',
                          trial: '7-day free trial',
                          features: [
                            '100 invoices per week',
                            '100 expenses per week',
                            '250 sales per week',
                            '100 products per week',
                            'Advanced reporting',
                            'Team management',
                            'All other features unlimited'
                          ],
                          popular: true,
                          color: 'green'
                        },
                        {
                          id: 'monthly',
                          name: 'Silver Monthly',
                          price: 450000,
                          displayPrice: '₦4,500',
                          period: '/month',
                          features: [
                            '450 invoices per month',
                            '450 expenses per month',
                            '1,000 sales per month',
                            '450 products per month',
                            'Advanced reporting',
                            'Team management',
                            'All other features unlimited'
                          ],
                          popular: false,
                          color: 'blue'
                        },
                        {
                          id: 'yearly',
                          name: 'Silver Yearly',
                          price: 5000000,
                          displayPrice: '₦50,000',
                          period: '/year',
                          savings: 'Save ₦4,000 annually',
                          features: [
                            '6,000 invoices per year',
                            '2,000 expenses per year',
                            '18,000 sales per year',
                            '2,000 products per year',
                            'Advanced reporting',
                            'Team management',
                            'Referral earnings (10% for 3 payments)',
                            'All other features unlimited'
                          ],
                          popular: false,
                          color: 'purple'
                        }
                      ].map((plan) => (
                        <Card 
                          key={plan.id} 
                          className={`relative border-2 transition-all duration-300 hover:shadow-xl ${
                            plan.popular 
                              ? 'border-green-500 shadow-lg transform scale-105' 
                              : 'border-gray-200 hover:border-green-400'
                          }`}
                        >
                          {plan.popular && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                              <span className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-medium shadow-lg">
                                Most Popular
                              </span>
                            </div>
                          )}
                          
                          <CardHeader className="text-center pb-4">
                            <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                              {plan.name}
                            </CardTitle>
                            <div className="text-4xl font-bold text-green-600 mb-2">
                              {plan.displayPrice}
                              <span className="text-sm font-normal text-gray-500">
                                {plan.period}
                              </span>
                            </div>
                            {plan.trial && (
                              <div className="flex items-center justify-center text-blue-600">
                                <Shield className="h-4 w-4 mr-1" />
                                <span className="text-sm font-medium">{plan.trial}</span>
                              </div>
                            )}
                            {plan.savings && (
                              <div className="text-green-600 text-sm font-medium bg-green-50 px-3 py-1 rounded-full">
                                {plan.savings}
                              </div>
                            )}
                          </CardHeader>
                          
                          <CardContent>
                            <ul className="space-y-3 mb-8">
                              {plan.features.map((feature, index) => (
                                <li key={index} className="flex items-start text-sm">
                                  <Shield className="h-4 w-4 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-700">{feature}</span>
                                </li>
                              ))}
                            </ul>
                            
                            <Button
                              className={`w-full text-white font-medium py-3 ${
                                plan.popular 
                                  ? 'bg-green-600 hover:bg-green-700 shadow-lg' 
                                  : 'bg-gray-600 hover:bg-gray-700'
                              } transition-all duration-200`}
                              onClick={() => {
                                // Navigate to subscription upgrade page
                                window.location.href = '/subscription-upgrade';
                              }}
                            >
                              Upgrade to {plan.name}
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </RoleBasedWrapper>
          </TabsContent>

          {/* System Tab - Only for Owners */}
          <TabsContent value="system">
            <RoleBasedWrapper allowedRoles={['owner']} fallback={
              <Card>
                <CardContent className="p-6">
                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertDescription>
                      Only business owners can access system administration tools. Contact your business owner if you need data consistency checks.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            }>
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="h-5 w-5" />
                      System Administration
                    </CardTitle>
                    <CardDescription>
                      Manage data integrity and system maintenance for your business
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Alert className="mb-4">
                      <Shield className="h-4 w-4" />
                      <AlertDescription>
                        These tools help maintain data consistency across your business records.
                        Run these checks regularly or after bulk data operations to ensure accuracy.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>

                <DataIntegrityWidget
                  onDataSync={() => {
                    // Refresh any cached data or trigger re-renders
                    toast.success('Data synchronization completed. All pages will reflect updated data.');
                  }}
                />
              </div>
            </RoleBasedWrapper>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
